/**
 * ExplorePlaces — /explore
 *
 * "Explore Places & Topics" — dual-search launchpad for the whole
 * knowledge graph. Left column: place search with country cascade.
 * Right column: free-form topic search across ALL entity types.
 * Both inputs show inline autocomplete suggestions AND, if no exact
 * prefix match, a "Did you mean X?" spelling-correction hint from
 * `/api/kg/didyoumean` (Python difflib).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe2, MapPin, ArrowRight, Sparkles, Loader2, Search, Wand2,
  Upload, PenLine, HandHeart,
} from "lucide-react";
import { http } from "@/lib/api";
import TopicNewsStrip from "@/components/TopicNewsStrip";

// Compact country → state → city map. Larger regions fall back to
// free-text entry via the search box below.
const GEO = {
  "India": {
    "Kerala":         ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Alappuzha", "Kollam"],
    "Karnataka":      ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi"],
    "Tamil Nadu":     ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli"],
    "Maharashtra":    ["Mumbai", "Pune", "Nagpur", "Nashik"],
    "Delhi":          ["New Delhi"],
    "West Bengal":    ["Kolkata", "Darjeeling"],
    "Rajasthan":      ["Jaipur", "Udaipur", "Jodhpur"],
    "Gujarat":        ["Ahmedabad", "Surat", "Vadodara"],
    "Uttar Pradesh":  ["Lucknow", "Varanasi", "Agra"],
    "Telangana":      ["Hyderabad", "Warangal"],
  },
  "United States": {
    "California":     ["Los Angeles", "San Francisco", "San Diego", "Sacramento"],
    "New York":       ["New York City", "Buffalo", "Rochester"],
    "Texas":          ["Houston", "Austin", "Dallas", "San Antonio"],
    "Illinois":       ["Chicago", "Springfield"],
    "Massachusetts":  ["Boston", "Cambridge"],
    "Washington":     ["Seattle", "Spokane"],
    "Oregon":         ["Portland", "Eugene"],
    "Florida":        ["Miami", "Orlando", "Tampa"],
    "Arizona":        ["Phoenix", "Tucson"],
    "Pennsylvania":   ["Philadelphia", "Pittsburgh"],
  },
  "United Kingdom": { "England": ["London", "Manchester", "Birmingham", "Liverpool"], "Scotland": ["Edinburgh", "Glasgow"], "Wales": ["Cardiff"] },
  "Japan":     { "Kanto": ["Tokyo", "Yokohama"], "Kansai": ["Osaka", "Kyoto"] },
  "France":    { "Île-de-France": ["Paris"], "Provence": ["Marseille", "Nice"] },
  "Germany":   { "Berlin": ["Berlin"], "Bavaria": ["Munich", "Nuremberg"] },
  "Australia": { "New South Wales": ["Sydney"], "Victoria": ["Melbourne"] },
  "Canada":    { "Ontario": ["Toronto", "Ottawa"], "Quebec": ["Montreal"] },
  "Brazil":    { "São Paulo": ["São Paulo"], "Rio de Janeiro": ["Rio de Janeiro"] },
  "South Africa": { "Gauteng": ["Johannesburg", "Pretoria"], "Western Cape": ["Cape Town"] },
};

const FEATURED_PLACES = [
  { name: "Kerala",              type: "state" },
  { name: "New Delhi",           type: "capital" },
  { name: "Tokyo",               type: "capital" },
  { name: "Paris",               type: "capital" },
  { name: "Amazon Rainforest",   type: "natural wonder" },
  { name: "Mount Everest",       type: "mountain" },
  { name: "Grand Canyon",        type: "canyon" },
  { name: "Great Barrier Reef",  type: "reef" },
];

const FEATURED_TOPICS = [
  { name: "Mahatma Gandhi",  entity_type: "person",       label: "Person" },
  { name: "Marie Curie",     entity_type: "person",       label: "Person" },
  { name: "United Nations",  entity_type: "organization", label: "Organization" },
  { name: "Olympic Games",   entity_type: "event",        label: "Event" },
  { name: "Type 2 Diabetes", entity_type: "disease",      label: "Disease" },
];

const PREFIX = {
  person: "/person", place: "/place", organization: "/organization",
  event: "/event", disease: "/disease",
};

export default function ExplorePlaces() {
  const nav = useNavigate();

  // Place-side state
  const [country, setCountry] = useState("");
  const [region,  setRegion]  = useState("");
  const [city,    setCity]    = useState("");
  const [placeQ,  setPlaceQ]  = useState("");
  const [placeSuggests, setPlaceSuggests] = useState([]);
  const [placeDym,      setPlaceDym]      = useState([]);
  const [placeBusy,     setPlaceBusy]     = useState(false);

  // Topic-side state
  const [topicQ,        setTopicQ]        = useState("");
  const [topicSuggests, setTopicSuggests] = useState([]);
  const [topicDym,      setTopicDym]      = useState([]);
  const [topicBusy,     setTopicBusy]     = useState(false);

  const [recentPlaces,  setRecentPlaces]  = useState([]);

  const countries = useMemo(() => Object.keys(GEO), []);
  const regions   = useMemo(() => country ? Object.keys(GEO[country] || {}) : [], [country]);
  const cities    = useMemo(() => (country && region) ? (GEO[country]?.[region] || []) : [], [country, region]);

  useEffect(() => {
    http.get("/kg/entities", { params: { entity_type: "place", limit: 8 } })
      .then(r => setRecentPlaces(r.data?.items || []))
      .catch(() => setRecentPlaces([]));
  }, []);

  // Debounced suggestions for both boxes
  useEffect(() => {
    const t = setTimeout(async () => {
      const q = placeQ.trim();
      if (!q) { setPlaceSuggests([]); setPlaceDym([]); return; }
      const s = await http.get("/kg/suggest", { params: { q, entity_type: "place" } }).then(r => r.data?.items || []).catch(() => []);
      setPlaceSuggests(s);
      if (s.length === 0) {
        const d = await http.get("/kg/didyoumean", { params: { q, entity_type: "place" } }).then(r => r.data?.items || []).catch(() => []);
        setPlaceDym(d);
      } else {
        setPlaceDym([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [placeQ]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = topicQ.trim();
      if (!q) { setTopicSuggests([]); setTopicDym([]); return; }
      const s = await http.get("/kg/suggest", { params: { q } }).then(r => r.data?.items || []).catch(() => []);
      setTopicSuggests(s);
      if (s.length === 0) {
        const d = await http.get("/kg/didyoumean", { params: { q } }).then(r => r.data?.items || []).catch(() => []);
        setTopicDym(d);
      } else {
        setTopicDym([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [topicQ]);

  const explorePlace = async (name, hint) => {
    if (!name?.trim()) return;
    setPlaceBusy(true);
    try {
      const r = await http.post("/kg/find-or-create", {
        name: name.trim(), entity_type: "place",
        hint: hint || [region, country].filter(Boolean).join(", ") || undefined,
      });
      const e = r.data?.entity;
      if (e?.slug) nav(`/place/${e.slug}`);
    } finally { setPlaceBusy(false); }
  };

  // Topic-side inference: guess type from label context; default = person.
  const exploreTopic = async (name, entityType = "person") => {
    if (!name?.trim()) return;
    setTopicBusy(true);
    try {
      const r = await http.post("/kg/find-or-create", { name: name.trim(), entity_type: entityType });
      const e = r.data?.entity;
      if (e?.slug) nav(`${PREFIX[e.entity_type] || "/kg/" + e.entity_type}/${e.slug}`);
    } finally { setTopicBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="explore-places">
      {/* Latest travel/heritage news */}
      <TopicNewsStrip topic="Places & discovery" q="travel OR destination OR heritage" testId="places-news-strip" />

      {/* Hero */}
      <section className="rounded-xl p-6 md:p-10 mb-8" style={{ background: "var(--wofy-surface)" }}>
        <div className="overline mb-2 inline-flex items-center gap-1.5">
          <Globe2 className="w-3.5 h-3.5"/>EXPLORE PLACES &amp; TOPICS
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
          🌎 Explore Places &amp; Topics
        </h1>
        <p className="max-w-3xl opacity-80 text-lg">
          Discover the world through people, places, history, culture, businesses, travel, education, and more.
        </p>
      </section>

      {/* Dual search — Places (left) + Topics (right) */}
      <section className="grid gap-4 md:grid-cols-2 mb-6" data-testid="explore-dual-search">
        {/* PLACE SEARCH */}
        <div className="wofy-card !p-4 flex flex-col" data-testid="explore-place-box">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4" style={{ color: "var(--wofy-primary)" }}/>
            <div className="font-medium">Search a Place</div>
            <span className="text-xs opacity-60">country + city + free-text</span>
          </div>

          <div className="grid gap-2 md:grid-cols-3 mb-2">
            <select
              value={country}
              onChange={e => { setCountry(e.target.value); setRegion(""); setCity(""); }}
              className="text-sm px-2 py-2 rounded border" style={{ borderColor: "var(--wofy-border)" }}
              data-testid="geo-country"
            >
              <option value="">Country…</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={region} onChange={e => { setRegion(e.target.value); setCity(""); }}
              disabled={!country}
              className="text-sm px-2 py-2 rounded border disabled:opacity-50"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="geo-region"
            >
              <option value="">Region…</option>
              {regions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={city} onChange={e => setCity(e.target.value)}
              disabled={!region}
              className="text-sm px-2 py-2 rounded border disabled:opacity-50"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="geo-city"
            >
              <option value="">City…</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="relative mb-2">
            <div className="flex items-center gap-2 wofy-card !p-2">
              <Search className="w-4 h-4 opacity-60 ml-1"/>
              <input
                value={placeQ}
                onChange={e => setPlaceQ(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") explorePlace(placeQ); }}
                placeholder="Or type any place — village, monument, river, park…"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="place-search-input"
              />
            </div>
            {placeSuggests.length > 0 && (
              <ul className="absolute left-0 right-0 mt-1 wofy-card !p-1 z-30 shadow-lg max-h-56 overflow-auto" data-testid="place-suggests">
                {placeSuggests.map(s => (
                  <li key={s.slug}>
                    <button onClick={() => nav(`/place/${s.slug}`)}
                      className="w-full text-left px-3 py-2 rounded hover:bg-black/5 text-sm inline-flex items-center gap-2 justify-between">
                      <span>{s.name}</span><ArrowRight className="w-3 h-3 opacity-60"/>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Did you mean */}
          {placeDym.length > 0 && (
            <div className="text-xs mb-2 opacity-80 flex items-center gap-2 flex-wrap" data-testid="place-didyoumean">
              <Wand2 className="w-3 h-3"/>Did you mean:
              {placeDym.slice(0, 3).map(d => (
                <button key={d.slug} onClick={() => nav(`/place/${d.slug}`)}
                        className="wofy-link underline underline-offset-2">
                  {d.name}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => explorePlace(placeQ || city || region || country)}
            disabled={placeBusy || !(placeQ || city || region || country)}
            className="wofy-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50 self-start"
            data-testid="place-explore-btn"
          >
            {placeBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4"/>}
            Explore {(placeQ || city || region || country) || "…"}
          </button>
        </div>

        {/* TOPIC SEARCH */}
        <div className="wofy-card !p-4 flex flex-col" data-testid="explore-topic-box">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }}/>
            <div className="font-medium">Search a Topic</div>
            <span className="text-xs opacity-60">people · organizations · events · diseases · anything</span>
          </div>

          <div className="relative mb-2">
            <div className="flex items-center gap-2 wofy-card !p-2">
              <Search className="w-4 h-4 opacity-60 ml-1"/>
              <input
                value={topicQ}
                onChange={e => setTopicQ(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") exploreTopic(topicQ); }}
                placeholder="e.g. Marie Curie, Ashwagandha, Olympic Games, ISRO…"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="topic-search-input"
              />
            </div>
            {topicSuggests.length > 0 && (
              <ul className="absolute left-0 right-0 mt-1 wofy-card !p-1 z-30 shadow-lg max-h-56 overflow-auto" data-testid="topic-suggests">
                {topicSuggests.map(s => (
                  <li key={s.slug}>
                    <button onClick={() => nav(`${PREFIX[s.entity_type] || "/kg/" + s.entity_type}/${s.slug}`)}
                      className="w-full text-left px-3 py-2 rounded hover:bg-black/5 text-sm inline-flex items-center gap-2 justify-between">
                      <span>{s.name}<span className="ml-2 text-xs opacity-60">· {s.entity_type}</span></span>
                      <ArrowRight className="w-3 h-3 opacity-60"/>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {topicDym.length > 0 && (
            <div className="text-xs mb-2 opacity-80 flex items-center gap-2 flex-wrap" data-testid="topic-didyoumean">
              <Wand2 className="w-3 h-3"/>Did you mean:
              {topicDym.slice(0, 3).map(d => (
                <button key={d.slug} onClick={() => nav(`${PREFIX[d.entity_type] || "/kg/" + d.entity_type}/${d.slug}`)}
                        className="wofy-link underline underline-offset-2">
                  {d.name}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => exploreTopic(topicQ, "person")}
              disabled={topicBusy || !topicQ.trim()}
              className="wofy-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="topic-explore-btn"
            >
              {topicBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
              Search &amp; write with AI
            </button>
            <span className="text-xs opacity-60">If not found, AI writes an article.</span>
          </div>
        </div>
      </section>

      {/* Instructional message */}
      <div className="wofy-card !p-4 mb-8 text-sm opacity-90 leading-relaxed" data-testid="explore-message">
        <p className="mb-2">
          <strong>Select a Country</strong> to explore its cities, communities, attractions, and local information, or <strong>enter any Topic</strong> to search across millions of articles, organizations, people, and resources on SocialPulse.
        </p>
        <p className="opacity-80"><em>Every search is the beginning of a new discovery.</em></p>
      </div>

      {/* Featured places */}
      <section className="mb-8">
        <h2 className="font-display text-2xl mb-3">Popular places</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURED_PLACES.map(p => (
            <button key={p.name} onClick={() => explorePlace(p.name)}
              className="wofy-card !p-4 hover:shadow-md transition text-left"
              data-testid={`featured-place-${p.name.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">{p.type}</div>
              <div className="font-medium inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{p.name}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Featured topics */}
      <section className="mb-8">
        <h2 className="font-display text-2xl mb-3">Popular topics</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {FEATURED_TOPICS.map(t => (
            <button key={t.name} onClick={() => exploreTopic(t.name, t.entity_type)}
              className="wofy-card !p-4 hover:shadow-md transition text-left"
              data-testid={`featured-topic-${t.name.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">{t.label}</div>
              <div className="font-medium">{t.name}</div>
            </button>
          ))}
        </div>
      </section>

      {recentPlaces.length > 0 && (
        <section className="mb-8">
          <h2 className="font-display text-2xl mb-3">Recently explored</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {recentPlaces.map(e => (
              <button key={e.slug} onClick={() => nav(`/place/${e.slug}`)}
                className="wofy-card !p-4 hover:shadow-md transition text-left"
                data-testid={`recent-place-${e.slug}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">place</div>
                <div className="font-medium inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{e.name}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Contribute / community CTA */}
      <section
        className="rounded-xl p-6 md:p-8 border-2"
        style={{ borderColor: "var(--wofy-primary)", background: "rgba(178,93,56,0.04)" }}
        data-testid="explore-contribute"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full grid place-items-center text-white shrink-0"
               style={{ background: "var(--wofy-primary)" }}>
            <HandHeart className="w-5 h-5"/>
          </div>
          <div>
            <div className="overline mb-1">✨ HELP BUILD OUR GLOBAL KNOWLEDGE COMMUNITY</div>
            <h2 className="font-display text-2xl">Share what you know.</h2>
          </div>
        </div>
        <p className="opacity-90 mb-3">
          Knowledge grows when people share it. If you notice missing information, have local insights, historical facts, photos, or updates, we invite you to contribute. Every approved contribution helps create a richer, more accurate resource for people around the world.
        </p>
        <p className="opacity-80 italic mb-4">
          Share your knowledge. Update a place. Improve a topic. Help others discover something new.
        </p>
        <div className="flex flex-wrap gap-2">
          <a href="/contact" className="wofy-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="contribute-btn">
            <PenLine className="w-4 h-4"/>Contribute
          </a>
          <a href="/contact" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5"
             style={{ borderColor: "var(--wofy-border)" }} data-testid="upload-photos-btn">
            <Upload className="w-4 h-4"/>Upload Photos
          </a>
          <a href="/contact" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5"
             style={{ borderColor: "var(--wofy-border)" }} data-testid="suggest-update-btn">
            <Sparkles className="w-4 h-4"/>Suggest an Update
          </a>
        </div>
      </section>
    </div>
  );
}
